/** A technology or platform whose logo can be shown next to a value that names it. */
export interface Brand {
  slug: string
  name: string
  /** The brand's own colour; absent for logos drawn in the surrounding text colour (they would vanish on one theme). */
  color?: string
  /** The logo is a full-colour image of its own (Google Cloud's four colours), not a one-colour shape. */
  multicolor?: boolean
}

const brands: Record<string, Brand> = {
  github: { slug: 'github', name: 'GitHub' },
  gitlab: { slug: 'gitlab', name: 'GitLab', color: '#FC6D26' },
  bitbucket: { slug: 'bitbucket', name: 'Bitbucket', color: '#2684FF' },
  gcp: { slug: 'googlecloud-color', name: 'Google Cloud', multicolor: true },
  aws: { slug: 'amazonaws', name: 'AWS', color: '#FF9900' },
  azure: { slug: 'microsoftazure', name: 'Azure', color: '#0078D4' },
  postgres: { slug: 'postgresql', name: 'PostgreSQL', color: '#4169E1' },
  mysql: { slug: 'mysql', name: 'MySQL', color: '#4479A1' },
  mongodb: { slug: 'mongodb', name: 'MongoDB', color: '#47A248' },
  mariadb: { slug: 'mariadb', name: 'MariaDB' },
  oracle: { slug: 'oracle', name: 'Oracle', color: '#F80000' },
  sqlserver: {
    slug: 'microsoftsqlserver',
    name: 'SQL Server',
    color: '#CC2927',
  },
  docker: { slug: 'docker', name: 'Docker', color: '#2496ED' },
  kubernetes: { slug: 'kubernetes', name: 'Kubernetes', color: '#326CE5' },
  spring: { slug: 'springboot', name: 'Spring Boot', color: '#6DB33F' },
  express: { slug: 'express', name: 'Express' },
  fastapi: { slug: 'fastapi', name: 'FastAPI', color: '#009688' },
  java: { slug: 'java-color', name: 'Java', multicolor: true },
  python: { slug: 'python', name: 'Python', color: '#3776AB' },
  typescript: { slug: 'typescript', name: 'TypeScript', color: '#3178C6' },
  node: { slug: 'nodedotjs', name: 'Node.js', color: '#5FA04E' },
  maven: { slug: 'apachemaven', name: 'Maven', color: '#C71A36' },
  npm: { slug: 'npm', name: 'npm', color: '#CB3837' },
  githubactions: {
    slug: 'githubactions',
    name: 'GitHub Actions',
    color: '#2088FF',
  },
  kong: { slug: 'kong', name: 'Kong' },
  apigee: { slug: 'apigee', name: 'Apigee', multicolor: true },
}

/** Ordered: the first pattern that matches names the brand. */
const patterns: Array<[RegExp, keyof typeof brands]> = [
  [/spring/i, 'spring'],
  [/fast\s*-?api/i, 'fastapi'],
  [/express/i, 'express'],
  [/typescript/i, 'typescript'],
  [/python|pytest/i, 'python'],
  [/\bjava\b|jdk|junit|checkstyle|\bpmd\b/i, 'java'],
  [/node(\.js)?\b/i, 'node'],
  [/maven/i, 'maven'],
  [/\bnpm\b/i, 'npm'],
  [/github\s*actions/i, 'githubactions'],
  [/github/i, 'github'],
  [/gitlab/i, 'gitlab'],
  [/bitbucket/i, 'bitbucket'],
  [/\bgcp\b|google|cloud\s*run|\bgke\b/i, 'gcp'],
  [/\baws\b|amazon|\beks\b|app\s*runner/i, 'aws'],
  [/azure|container\s*apps/i, 'azure'],
  [/postgres/i, 'postgres'],
  [/mysql/i, 'mysql'],
  [/mongo/i, 'mongodb'],
  [/mariadb/i, 'mariadb'],
  [/oracle/i, 'oracle'],
  [/sql\s*server|mssql/i, 'sqlserver'],
  [/kubernetes|\bk8s\b/i, 'kubernetes'],
  [/docker|container/i, 'docker'],
  [/kong/i, 'kong'],
  [/apigee/i, 'apigee'],
]

/** The brand a value names ("SPRING_BOOT", "GitHub", "Cloud Run", "PostgreSQL"), or null when it names none. */
export function brandFor(value: string | null | undefined): Brand | null {
  const text = (value ?? '').replaceAll('_', ' ').trim()
  if (!text) return null
  for (const [pattern, key] of patterns) {
    if (pattern.test(text)) return brands[key] ?? null
  }
  return null
}
